/**
 * Django admin_stats ve eski/alternatif payload şekillerini tek dashboard modeline normalize eder.
 * - Dizi gelirse ilk obje kullanılır (boş ise {}).
 * - { data: { ... } } sarılmış yanıtlar açılır.
 */

function unwrapSource(raw) {
    if (raw == null) return {};
    if (typeof raw !== "object") return {};
    if (Array.isArray(raw)) {
        const first = raw[0];
        if (first && typeof first === "object" && !Array.isArray(first)) return first;
        return {};
    }
    if (
        raw.data &&
        typeof raw.data === "object" &&
        !Array.isArray(raw.data)
    ) {
        return raw.data;
    }
    return raw;
}

function pickNumber(src, keys) {
    if (!src || typeof src !== "object") return 0;
    for (const k of keys) {
        if (!(k in src)) continue;
        const v = src[k];
        if (v === undefined || v === null || v === "") continue;
        const n = typeof v === "number" ? v : Number(v);
        if (!Number.isNaN(n)) return n;
    }
    return 0;
}

function pickNested(src, path, fallbacks = []) {
    if (!src || typeof src !== "object") return 0;
    let cur = src;
    for (const k of path) {
        if (cur == null || typeof cur !== "object") return 0;
        cur = cur[k];
    }
    if (typeof cur === "number" && Number.isFinite(cur)) return cur;
    if (cur !== undefined && cur !== null && cur !== "") {
        const n = Number(cur);
        if (!Number.isNaN(n)) return n;
    }
    return pickNumber(src, fallbacks);
}

function pickArray(src, key, fallbacks = []) {
    const keys = [key, ...fallbacks];
    for (const k of keys) {
        const v = src?.[k];
        if (Array.isArray(v)) return v;
    }
    return [];
}

export function hydrateStatsPayload(raw) {
    const src = unwrapSource(raw);

    const turRaw =
        src.tur_dagilim && typeof src.tur_dagilim === "object"
            ? src.tur_dagilim
            : src.turDagilim;

    const tur =
        turRaw && typeof turRaw === "object" && !Array.isArray(turRaw)
            ? {
                  trafik: Number(turRaw.trafik ?? 0),
                  deger_kaybi: Number(
                      turRaw.deger_kaybi ?? turRaw.degerKaybi ?? 0,
                  ),
                  kasko: Number(turRaw.kasko ?? 0),
              }
            : { trafik: 0, deger_kaybi: 0, kasko: 0 };

    const aylar = pickArray(src, "aylar");
    const aylikDosya = pickArray(src, "aylikDosya", ["aylik_dosya"]);

    const out = {
        ...src,
        muvekkil: pickNumber(src, ["muvekkil", "muvekkil_sayisi"]),
        dosya: pickNumber(src, ["dosya", "sigorta_sayisi"]),
        degerKaybi: pickNumber(src, ["degerKaybi", "deger_kaybi_sayisi"]),
        araci: pickNumber(src, ["araci", "araci_sayisi"]),
        buAyMuvekkil: pickNumber(src, [
            "buAyMuvekkil",
            "bu_ay_muvekkil",
            "ay_muvekkil_sayisi",
        ]),
        buAySigortaDosya: pickNumber(src, [
            "buAySigortaDosya",
            "bu_ay_sigorta_dosya",
            "ay_sigorta_sayisi",
        ]),
        gecenAyMuvekkil: pickNumber(src, [
            "gecenAyMuvekkil",
            "gecen_ay_muvekkil",
        ]),
        gecenAySigortaDosya: pickNumber(src, [
            "gecenAySigortaDosya",
            "gecen_ay_sigorta_dosya",
        ]),
        dosyaSon6Ay: pickNumber(src, ["dosyaSon6Ay", "dosya_son6_ay"]),
        dosyaSon12Ay: pickNumber(src, ["dosyaSon12Ay", "dosya_son12_ay"]),
        yatarKonuSayisi: pickNumber(src, [
            "yatarKonuSayisi",
            "yatar_konu_sayisi",
        ]),
        tahkimSurecSayisi: pickNumber(src, [
            "tahkimSurecSayisi",
            "tahkim_surec_sayisi",
            "tahkim_dosya_sayisi",
        ]),
        muvekkilSon6Ay: pickNumber(src, [
            "muvekkilSon6Ay",
            "muvekkil_son6_ay",
        ]),
        yatarAcilacakSayisi: pickNumber(src, [
            "yatarAcilacakSayisi",
            "yatar_acilacak_sayisi",
        ]),
        bekleyenBasvuru16Gun: pickNumber(src, [
            "bekleyenBasvuru16Gun",
            "bekleyen_basvuru_16gun",
        ]),
        basvurudaSayisi: pickNumber(src, [
            "basvurudaSayisi",
            "basvuruda_sayisi",
        ]),
        tahkimYaklasanBasvuruSayisi: pickNumber(src, [
            "tahkimYaklasanBasvuruSayisi",
            "tahkim_yaklasan_basvuru_sayisi",
        ]),
        tahkimYaklasanSayisi: pickNumber(src, [
            "tahkimYaklasanSayisi",
            "tahkim_yaklasan_sayisi",
        ]),
        yeniDosya7Gun: pickNumber(src, [
            "yeniDosya7Gun",
            "yeni_dosya_7_gun",
        ]),
        bildirimOzetToplam: pickNumber(src, [
            "bildirimOzetToplam",
            "bildirim_ozet_toplam",
        ]),
        turDagilim: tur,
        aylar,
        aylikDosya,
        uyariSatirlari: pickArray(src, "uyariSatirlari", [
            "uyari_satirlari",
        ]),
        yeniMuvekkilSatirlari: pickArray(src, "yeniMuvekkilSatirlari", [
            "yeni_muvekkil_satirlari",
        ]),
        yeniSigortaSatirlari: pickArray(src, "yeniSigortaSatirlari", [
            "yeni_sigorta_satirlari",
        ]),
        islemLoglari: pickArray(src, "islemLoglari", ["islem_loglari"]),
        gecikmisTahkimSatirlari: pickArray(
            src,
            "gecikmisTahkimSatirlari",
            ["gecikmis_tahkim_satirlari"],
        ),
        yaklasanTahkimSatirlari: pickArray(
            src,
            "yaklasanTahkimSatirlari",
            ["yaklasan_tahkim_satirlari"],
        ),
        tahkimeGidecekSatirlari: pickArray(
            src,
            "tahkimeGidecekSatirlari",
            ["tahkime_gidecek_satirlari"],
        ),
        basvurudaSatirlari: pickArray(src, "basvurudaSatirlari", [
            "basvuruda_satirlari",
        ]),
        tahkimYaklasanBasvuruSatirlari: pickArray(
            src,
            "tahkimYaklasanBasvuruSatirlari",
            ["tahkim_yaklasan_basvuru_satirlari"],
        ),
        yeniDosya7GunSatirlari: pickArray(src, "yeniDosya7GunSatirlari", [
            "yeni_dosya_7_gun_satirlari",
        ]),
        yaklasanTahkimSlaSatirlari: pickArray(
            src,
            "yaklasanTahkimSlaSatirlari",
            ["yaklasan_tahkim_sla_satirlari"],
        ),
        yatarDosyaSatirlari: pickArray(src, "yatarDosyaSatirlari", [
            "yatar_dosya_satirlari",
        ]),
        tahkimDosyaSatirlari: pickArray(src, "tahkimDosyaSatirlari", [
            "tahkim_dosya_satirlari",
        ]),
        odemeDosyaLiderleri: pickArray(src, "odemeDosyaLiderleri", [
            "odeme_dosya_liderleri",
        ]),
        odemeOzet: (() => {
            const oz =
                src.odemeOzet && typeof src.odemeOzet === "object"
                    ? src.odemeOzet
                    : {};
            return {
                kayitSayisi: pickNumber(oz, ["kayitSayisi", "kayit_sayisi"]),
                odemeMiktariToplam: pickNumber(oz, [
                    "odemeMiktariToplam",
                    "odeme_miktari_toplam",
                ]),
                mbsHakedisToplam: pickNumber(oz, [
                    "mbsHakedisToplam",
                    "mbs_hakedis_toplam",
                ]),
                sigortaliHakedisToplam: pickNumber(oz, [
                    "sigortaliHakedisToplam",
                    "sigortali_hakedis_toplam",
                ]),
                toplamMasraf: pickNumber(oz, ["toplamMasraf", "toplam_masraf"]),
                odemeYapildiSayisi: pickNumber(oz, [
                    "odemeYapildiSayisi",
                    "odeme_yapildi_sayisi",
                ]),
            };
        })(),
        tahkim_bekleyen: pickNumber(src, [
            "tahkim_bekleyen",
            "tahkimSurecSayisi",
            "tahkim_surec_sayisi",
        ]),
        yeni_muvekkil_30: pickNumber(src, [
            "yeni_muvekkil_30",
            "buAyMuvekkil",
            "bu_ay_muvekkil",
        ]),
        yeni_dosya_30: pickNumber(src, [
            "yeni_dosya_30",
            "buAySigortaDosya",
            "bu_ay_sigorta_dosya",
        ]),
        randevu_bugun: pickNumber(src, ["randevu_bugun"]),
        odeme_bekleyen: pickNumber(src, ["odeme_bekleyen", "bekleyen_odeme"]),
        bekleyen_odeme_listesi: pickArray(src, "bekleyen_odeme_listesi", [
            "bekleyenOdemeListesi",
        ]),
        fatura_bekleyen: pickNumber(src, ["fatura_bekleyen", "bekleyen_fatura"]),
        hatirlatma_bugun: pickNumber(src, [
            "hatirlatma_bugun",
            "aktif_hatirlatma",
        ]),
        yatar_dosyalar: src.yatar_dosyalar || {
            yatar: pickNumber(src, ["yatarAcilacakSayisi", "yatar_konu_sayisi"]),
        },
        yatar: pickNested(src, ["yatar_dosyalar", "yatar"], [
            "yatarAcilacakSayisi",
            "yatar_konu_sayisi",
        ]),
        tahsilat_toplam: src.tahsilat_toplam || {
            tahsilat: pickNumber(src.tahsilat_toplam || src, [
                "tahsilat",
                "odemeMiktariToplam",
            ]),
        },
        tahsilat: pickNested(src, ["tahsilat_toplam", "tahsilat"], [
            "tahsilat",
            "odemeMiktariToplam",
        ]),
        previous: src.previous || null,
        durumDagilim: src.durumDagilim || src.durum_dagilim || {},
        topSirketler: pickArray(src, "topSirketler", ["top_sirketler"]),
        tahkimDosyalari: pickArray(src, "tahkimDosyalari", [
            "tahkim_dosya_satirlari",
            "tahkimDosyaSatirlari",
        ]),
        yatarDosyalari: pickArray(src, "yatarDosyalari", [
            "yatar_dosya_satirlari",
            "yatarDosyaSatirlari",
        ]),
        kritikDosyalar: pickArray(src, "kritikDosyalar", [
            "criticalCases",
            "alerts",
        ]),
        bugunAjanda: pickArray(src, "bugunAjanda", ["todayAgenda"]),
        aktivite: pickArray(src, "aktivite", ["recentActivity", "islemLoglari"]),
        advanced_notifications: pickArray(src, "advanced_notifications"),
        diagnostics: src.diagnostics || null,
        user_display: src.user_display || src.userDisplay || "",
        finance_summary: src.finance_summary || src.financeSummary || null,
        basvuruKonusuDagilim: pickArray(src, "basvuruKonusuDagilim", [
            "basvuru_konusu_dagilim",
        ]),
        pipeline: pickArray(src, "pipeline"),
        eksik_evrak_stats: pickArray(src, "eksik_evrak_stats", [
            "eksikEvrakStats",
        ]),
        sla_alerts: pickArray(src, "sla_alerts", ["slaAlerts"]),
        avukat_performance: pickArray(src, "avukat_performance", [
            "avukatPerformance",
        ]),
        week_calendar: pickArray(src, "week_calendar", ["weekCalendar"]),
        month_comparison: src.month_comparison || src.monthComparison || null,
        today_tasks: pickArray(src, "today_tasks", ["todayTasks"]),
        command_hub: pickArray(src, "command_hub", ["commandHub"]),
    };

    if (!out.chartData?.length && out.aylar?.length) {
        out.chartData = out.aylar.map((name, i) => ({
            name,
            dosya: Number(out.aylikDosya?.[i] ?? 0),
            muvekkil: 0,
        }));
    } else {
        out.chartData = pickArray(src, "chartData");
    }

    if (!out.aktivite?.length && out.islemLoglari?.length) {
        out.aktivite = out.islemLoglari.map((row) => ({
            baslik: row.baslik,
            detay: row.detay,
            tip: (row.tip || "").toLowerCase(),
            tarih: row.zaman || row.tarih,
            timestamp: row.zaman || row.timestamp,
        }));
    }

    if (!out.tahkimDosyalari?.length && out.tahkimDosyaSatirlari?.length) {
        out.tahkimDosyalari = out.tahkimDosyaSatirlari.map((r) => ({
            dosyaNo: r.dosyaNo,
            adSoyad: r.muvekkilAdSoyad,
            gun: r.kalanGun,
            type: "TAHKIM",
        }));
    }

    if (!out.yatarDosyalari?.length && out.yatarDosyaSatirlari?.length) {
        out.yatarDosyalari = out.yatarDosyaSatirlari.map((r) => ({
            dosyaNo: r.dosyaNo,
            adSoyad: r.muvekkilAdSoyad,
            gun: r.kalanGun,
            type: "YATAR",
        }));
    }

    return out;
}
